import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Link,
  Rating,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import { useAuth } from '../../contexts/AuthContext';
import { useShop } from '../../contexts/ShopContext';
import { AnimatedText, Counter, Reveal, Stagger, StaggerItem } from '../../motion';
import { fadeUp, hoverLift, staggerItemTight } from '../../motion/variants';
import { useMotionSafe } from '../../motion/useMotionSafe';
import { duration, fontFamilyDisplay, glory, grey, hazard, ms, radius, stagger } from '../../theme/tokens';
import { weeklyHoursLines } from '../../utils/shopHours';
import EngineTeardown from './home/EngineTeardown';
import GarageHeading from './home/GarageHeading';
import GearDrawing from './home/GearDrawing';
import { crew, guarantees, reviews, services, stats } from './home/content';
import {
  draftingGrid,
  gaugeNumber,
  hazardStripe,
  headline,
  prose,
  red,
  redButton,
  rivets,
  steel,
  steelButton,
  stencil,
} from './home/garage';

interface VisitLine {
  text: string;
  href?: string;
}

/** "+" and "★" are marks; "mo" is a unit. Both sit smaller than the figure. */
const suffixSx = (suffix: string) =>
  /[a-z]/i.test(suffix)
    ? { fontSize: '0.42em', color: grey[400], letterSpacing: '0.06em', textTransform: 'uppercase' as const, ml: 0.5 }
    : { fontSize: '0.62em', color: suffix.includes('★') ? hazard[500] : glory[400], ml: 0.25 };

const initials = (name: string) =>
  name
    .split(' ')
    .map((part) => part[0])
    .join('');

const HomePage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { selectedShop } = useShop();
  const theme = useTheme();
  const { enabled: motionEnabled } = useMotionSafe();
  const isDark = theme.palette.mode === 'dark';
  const accent = red(isDark);
  const paperTone = isDark ? 'dark' : 'light';
  const steelLine = `1px solid ${alpha(grey[0], 0.1)}`;
  const dashed = `1px dashed ${theme.palette.divider}`;

  const shopPath = (path: string) =>
    selectedShop ? `/shop/${selectedShop.name.toLowerCase().replace(/\s+/g, '-')}/${path}` : `/${path}`;

  const phone = selectedShop?.phoneNumber;
  const telHref = phone ? `tel:${phone.replace(/[^\d+]/g, '')}` : undefined;

  const visit: { icon: React.ReactNode; title: string; lines: VisitLine[] }[] = [
    {
      icon: <PlaceOutlinedIcon />,
      title: 'Find us',
      lines: selectedShop
        ? [
            {
              text: selectedShop.address,
              href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedShop.address)}`,
            },
          ]
        : [{ text: '1234 Auto Service Drive' }, { text: 'Springfield, IL 62704' }],
    },
    {
      icon: <ScheduleIcon />,
      title: 'Shop hours',
      lines: (selectedShop?.businessHours ? weeklyHoursLines(selectedShop.businessHours) : ['Call for hours']).map(
        (text) => ({ text }),
      ),
    },
    {
      icon: <PhoneOutlinedIcon />,
      title: 'Call the shop',
      lines: phone
        ? [
            { text: phone, href: telHref },
            ...(selectedShop?.email ? [{ text: selectedShop.email, href: `mailto:${selectedShop.email}` }] : []),
          ]
        : [{ text: '(555) 123-4567' }, { text: 'service@mechanix.com' }],
    },
  ];

  return (
    <Box>
      {/* ------------------------------------------------------------------ */}
      {/* Hero: dark steel in both colour modes                               */}
      {/* ------------------------------------------------------------------ */}
      <Box component="section" sx={{ ...steel, position: 'relative', overflow: 'hidden' }}>
        {/* A red work light behind the drawing */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            right: { md: '-18%', lg: '-6%' },
            top: '-4%',
            width: 760,
            height: 760,
            borderRadius: '50%',
            display: { xs: 'none', md: 'block' },
            pointerEvents: 'none',
            background: `radial-gradient(circle, ${alpha(glory[600], 0.17)} 0%, transparent 64%)`,
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: '50%',
            right: { md: -250, lg: -150, xl: -40 },
            transform: { md: 'translateY(-50%) scale(0.78)', lg: 'translateY(-50%)' },
            transformOrigin: 'right center',
            display: { xs: 'none', md: 'block' },
            pointerEvents: 'none',
          }}
        >
          <GearDrawing size={720} />
        </Box>

        <Container sx={{ position: 'relative', pt: { xs: 7, md: 13 }, pb: { xs: 8, md: 13 } }}>
          <Reveal variants={fadeUp}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{
                display: 'inline-flex',
                mb: { xs: 3, md: 4 },
                px: 1.5,
                py: 0.75,
                border: `1px solid ${alpha(grey[0], 0.14)}`,
                borderRadius: `${radius.xs}px`,
                backgroundColor: alpha(grey[0], 0.03),
              }}
            >
              <Typography component="span" aria-hidden sx={{ color: glory[400], letterSpacing: '0.25em', fontSize: '0.75rem' }}>
                ★★★
              </Typography>
              <Typography component="p" sx={{ ...stencil, color: grey[300] }}>
                {selectedShop?.name ?? 'MechaniX'} · Independent American repair shop
              </Typography>
            </Stack>
          </Reveal>

          <AnimatedText
            variant="h1"
            component="h1"
            by="line"
            text={'Honest work.\nBuilt to last.'}
            highlight={['Built to last.']}
            highlightSx={{ color: glory[400] }}
            interval={stagger.loose}
            sx={{
              ...headline,
              color: grey[50],
              fontSize: { xs: '3rem', sm: '4.5rem', md: '5.75rem', lg: '6.5rem' },
              lineHeight: 0.9,
              mb: { xs: 3, md: 3.5 },
            }}
          />

          <Reveal variants={fadeUp} delay={0.35}>
            <Typography
              sx={{
                ...prose,
                color: grey[300],
                fontSize: { xs: '1.0625rem', md: '1.25rem' },
                lineHeight: 1.6,
                maxWidth: '46ch',
                mb: { xs: 4, md: 5 },
              }}
            >
              Trucks, daily drivers and weekend projects — diagnosed right the first time, fixed by
              ASE-certified techs, and backed by a{' '}
              <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                24-month / 24,000-mile
              </Box>{' '}
              warranty.
            </Typography>
          </Reveal>

          <Reveal variants={fadeUp} delay={0.5}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
              {isAuthenticated ? (
                <Button component={RouterLink} to="/admin/appointments" endIcon={<ArrowForwardIcon />} sx={redButton}>
                  Go to dashboard
                </Button>
              ) : (
                <>
                  <Button
                    component={RouterLink}
                    to={shopPath('book-appointment')}
                    endIcon={<ArrowForwardIcon />}
                    sx={redButton}
                  >
                    Book a service
                  </Button>
                  <Button component={RouterLink} to="/shops" sx={steelButton}>
                    Find a shop
                  </Button>
                </>
              )}
            </Stack>
          </Reveal>
        </Container>

        <Box aria-hidden sx={hazardStripe(12)} />

        {/* Gauges */}
        <Box sx={{ backgroundColor: grey[900], boxShadow: `inset 0 1px 0 ${alpha(grey[0], 0.05)}` }}>
          <Container>
            <Stagger
              whenInView
              interval={stagger.base}
              sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' } }}
            >
              {stats.map((stat, index) => (
                <StaggerItem
                  key={stat.label}
                  sx={{
                    py: { xs: 3, md: 4.5 },
                    px: 2,
                    textAlign: 'center',
                    borderLeft: { xs: index % 2 ? steelLine : 'none', md: index ? steelLine : 'none' },
                    borderTop: { xs: index >= 2 ? steelLine : 'none', md: 'none' },
                  }}
                >
                  <Typography
                    component="p"
                    sx={{
                      ...gaugeNumber,
                      color: grey[50],
                      fontSize: { xs: '2.75rem', md: '3.5rem' },
                      lineHeight: 1,
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'center',
                    }}
                  >
                    <Counter value={stat.value} decimals={stat.decimals ?? 0} />
                    <Box component="span" sx={suffixSx(stat.suffix)}>
                      {stat.suffix.trim()}
                    </Box>
                  </Typography>
                  <Typography sx={{ ...stencil, color: grey[400], mt: 1.25 }}>{stat.label}</Typography>
                </StaggerItem>
              ))}
            </Stagger>
          </Container>
        </Box>
      </Box>

      <EngineTeardown />

      {/* ------------------------------------------------------------------ */}
      {/* Services, on drafting paper                                         */}
      {/* ------------------------------------------------------------------ */}
      <Box component="section" sx={{ position: 'relative', overflow: 'hidden' }}>
        <Box aria-hidden sx={draftingGrid(alpha(theme.palette.text.primary, isDark ? 0.05 : 0.045))} />
        <Container sx={{ position: 'relative', py: { xs: 9, md: 14 } }}>
          <GarageHeading
            layout="split"
            eyebrow="What we work on"
            title={'Every make.\nEvery model.\nEvery mile.'}
            highlight={['Every mile.']}
            description="Domestic or import, gas or diesel — if it rolls into the bay, we can diagnose it and fix it right."
          />

          <Stagger
            whenInView
            interval={stagger.base}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 3,
            }}
          >
            {services.map((service, index) => (
              <StaggerItem key={service.title} {...(motionEnabled ? hoverLift : {})} sx={{ height: '100%' }}>
                <Card
                  sx={{
                    height: '100%',
                    overflow: 'hidden',
                    borderRadius: `${radius.xs}px`,
                    transition: `border-color ${ms(duration.base)}`,
                    '&:hover': { borderColor: alpha(glory[600], 0.45) },
                    ...rivets(paperTone),
                  }}
                >
                  <Box aria-hidden sx={{ height: 4, backgroundColor: glory[600] }} />
                  <CardContent sx={{ p: 3.5, pt: 3.25 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          display: 'grid',
                          placeItems: 'center',
                          borderRadius: `${radius.xs}px`,
                          color: glory[400],
                          backgroundColor: isDark ? grey[950] : grey[900],
                          // Pressed into the plate: a lit top edge and a shadowed base.
                          boxShadow: `inset 0 1px 0 ${alpha(grey[0], 0.1)}, inset 0 -3px 0 ${alpha(grey[950], 0.6)}`,
                          '& svg': { fontSize: 30 },
                        }}
                      >
                        {service.icon}
                      </Box>
                      <Typography
                        aria-hidden
                        sx={{
                          ...gaugeNumber,
                          fontSize: '3rem',
                          lineHeight: 1,
                          color: 'transparent',
                          WebkitTextStroke: `1.25px ${alpha(theme.palette.text.primary, 0.26)}`,
                        }}
                      >
                        {String(index + 1).padStart(2, '0')}
                      </Typography>
                    </Stack>

                    <Typography component="h3" sx={{ ...headline, fontSize: '1.75rem', lineHeight: 1, mb: 1.25 }}>
                      {service.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ ...prose, mb: 2.75, lineHeight: 1.6 }}>
                      {service.description}
                    </Typography>

                    <Box component="ul" sx={{ m: 0, p: 0, pt: 2, listStyle: 'none', borderTop: dashed }}>
                      {service.covers.map((item) => (
                        <Box component="li" key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.6 }}>
                          <Box aria-hidden sx={{ width: 6, height: 6, flexShrink: 0, backgroundColor: accent }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </StaggerItem>
            ))}
          </Stagger>
        </Container>
      </Box>

      {/* ------------------------------------------------------------------ */}
      {/* Why us: the shop's checklist                                        */}
      {/* ------------------------------------------------------------------ */}
      <Box component="section" sx={{ backgroundColor: 'background.paper', borderBlock: `1px solid ${theme.palette.divider}` }}>
        <Container sx={{ py: { xs: 9, md: 14 } }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: { xs: 1, md: 10 },
              alignItems: 'center',
            }}
          >
            <GarageHeading
              eyebrow="Why folks come back"
              title="No shortcuts. No runaround."
              highlight={['runaround']}
              description="We show you what we found before we start, text you photos from the bay, and send a link so you can follow the job from drop-off to pickup."
            />

            <Reveal variants={fadeUp}>
              <Card sx={{ borderRadius: `${radius.xs}px`, overflow: 'hidden' }}>
                <Box sx={{ ...steel, px: { xs: 3, md: 3.5 }, py: 2, borderBottom: steelLine }}>
                  <Typography component="p" sx={{ ...stencil, color: grey[100] }}>
                    Every job, every time
                  </Typography>
                </Box>
                <Stagger whenInView interval={stagger.tight} sx={{ px: { xs: 3, md: 3.5 }, py: 1 }}>
                  {guarantees.map((item, index) => (
                    <StaggerItem key={item} variants={staggerItemTight}>
                      <Stack
                        direction="row"
                        spacing={1.75}
                        alignItems="center"
                        sx={{ py: 1.6, borderBottom: index < guarantees.length - 1 ? dashed : 'none' }}
                      >
                        <CheckBoxIcon sx={{ color: accent, fontSize: 24 }} />
                        <Typography sx={{ fontWeight: 600 }}>{item}</Typography>
                      </Stack>
                    </StaggerItem>
                  ))}
                </Stagger>
              </Card>
            </Reveal>
          </Box>
        </Container>
      </Box>

      {/* ------------------------------------------------------------------ */}
      {/* Reviews                                                             */}
      {/* ------------------------------------------------------------------ */}
      <Container component="section" sx={{ py: { xs: 9, md: 14 } }}>
        <GarageHeading eyebrow="Word of mouth" title="What folks are saying" highlight={['saying']} layout="center" />

        <Stagger
          whenInView
          interval={stagger.base}
          sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}
        >
          {reviews.map((review) => (
            <StaggerItem key={review.name} sx={{ height: '100%' }}>
              <Card sx={{ height: '100%', position: 'relative', borderRadius: `${radius.xs}px` }}>
                <FormatQuoteIcon
                  aria-hidden
                  sx={{ position: 'absolute', top: 14, right: 16, fontSize: 64, color: alpha(accent, isDark ? 0.22 : 0.14) }}
                />
                <CardContent sx={{ p: 3.5, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Rating value={review.rating} readOnly size="small" sx={{ color: hazard[500], mb: 2.25 }} />
                  <Typography sx={{ ...prose, fontSize: '1.0625rem', lineHeight: 1.65, flexGrow: 1, mb: 3 }}>
                    {review.text}
                  </Typography>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pt: 2.5, borderTop: dashed }}>
                    <Box
                      aria-hidden
                      sx={{
                        ...gaugeNumber,
                        width: 42,
                        height: 42,
                        flexShrink: 0,
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: `${radius.xs}px`,
                        backgroundColor: grey[900],
                        color: grey[50],
                        fontSize: '1.125rem',
                        letterSpacing: '0.04em',
                      }}
                    >
                      {initials(review.name)}
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, lineHeight: 1.3 }}>{review.name}</Typography>
                      <Typography sx={{ ...stencil, color: 'text.secondary', mt: 0.25 }}>{review.vehicle}</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </StaggerItem>
          ))}
        </Stagger>
      </Container>

      {/* ------------------------------------------------------------------ */}
      {/* Crew: name patches on steel                                         */}
      {/* ------------------------------------------------------------------ */}
      <Box component="section" sx={steel}>
        <Box aria-hidden sx={hazardStripe(8)} />
        <Container sx={{ py: { xs: 9, md: 14 } }}>
          <GarageHeading
            onSteel
            layout="center"
            eyebrow="The crew"
            title="Techs who show their work"
            highlight={['show', 'their', 'work']}
            description="ASE-certified, years on the tools, and happy to walk you under the lift."
          />

          <Stagger
            whenInView
            interval={stagger.base}
            sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3 }}
          >
            {crew.map((member) => (
              <StaggerItem key={member.name} {...(motionEnabled ? hoverLift : {})} sx={{ height: '100%' }}>
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    color: grey[100],
                    backgroundColor: grey[900],
                    border: steelLine,
                    borderRadius: `${radius.xs}px`,
                    boxShadow: `inset 0 1px 0 ${alpha(grey[0], 0.06)}`,
                    ...rivets('dark'),
                  }}
                >
                  <CardContent sx={{ p: 3.5, pt: 4 }}>
                    {/* The oval name patch on a work shirt */}
                    <Box
                      sx={{
                        mx: 'auto',
                        mb: 2.75,
                        width: 156,
                        height: 64,
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: '50%',
                        backgroundColor: grey[50],
                        border: `3px solid ${glory[600]}`,
                        outline: `1.5px dashed ${alpha(glory[600], 0.55)}`,
                        outlineOffset: -8,
                        boxShadow: `0 3px 0 ${alpha(grey[950], 0.55)}`,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: fontFamilyDisplay,
                          fontWeight: 800,
                          fontStyle: 'italic',
                          fontSize: '1.625rem',
                          letterSpacing: '0.03em',
                          textTransform: 'uppercase',
                          color: glory[700],
                        }}
                      >
                        {member.name.split(' ')[0]}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.125rem', color: grey[50] }}>{member.name}</Typography>
                    <Typography sx={{ ...stencil, color: glory[400], mt: 0.5 }}>{member.role}</Typography>
                    <Typography variant="body2" sx={{ color: grey[300], mt: 1.25 }}>
                      {member.specialty}
                    </Typography>
                    <Typography sx={{ ...stencil, color: grey[400], mt: 0.5 }}>{member.years} yrs</Typography>
                    <Stack direction="row" spacing={0.75} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mt: 2.25 }}>
                      {member.certifications.map((cert) => (
                        <Box
                          key={cert}
                          component="span"
                          sx={{
                            ...stencil,
                            fontSize: '0.75rem',
                            letterSpacing: '0.1em',
                            color: grey[200],
                            border: `1px solid ${alpha(grey[0], 0.22)}`,
                            borderRadius: `${radius.xs}px`,
                            backgroundColor: alpha(grey[0], 0.03),
                            px: 1,
                            py: 0.25,
                          }}
                        >
                          {cert}
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </StaggerItem>
            ))}
          </Stagger>
        </Container>
      </Box>

      {/* ------------------------------------------------------------------ */}
      {/* Visit: one plate, three columns                                     */}
      {/* ------------------------------------------------------------------ */}
      <Container component="section" sx={{ py: { xs: 9, md: 14 } }}>
        <GarageHeading eyebrow="Come on by" title="Swing by the shop" highlight={['shop']} />

        <Reveal variants={fadeUp}>
          <Card sx={{ borderRadius: `${radius.xs}px`, ...rivets(paperTone) }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
              {visit.map((card, index) => (
                <Box
                  key={card.title}
                  sx={{
                    p: { xs: 3, md: 4 },
                    borderLeft: { md: index ? dashed : 'none' },
                    borderTop: { xs: index ? dashed : 'none', md: 'none' },
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      mb: 2,
                      display: 'grid',
                      placeItems: 'center',
                      color: accent,
                      border: `2px solid ${accent}`,
                      borderRadius: `${radius.xs}px`,
                      '& svg': { fontSize: 22 },
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Typography component="h3" sx={{ ...stencil, fontSize: '0.9375rem', color: 'text.primary', mb: 1 }}>
                    {card.title}
                  </Typography>
                  {card.lines.map((line) => (
                    <Typography key={line.text} sx={{ color: 'text.secondary', lineHeight: 1.75 }}>
                      {line.href ? (
                        <Link
                          href={line.href}
                          color="inherit"
                          underline="hover"
                          {...(line.href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                          sx={{ '&:hover': { color: 'text.primary' } }}
                        >
                          {line.text}
                        </Link>
                      ) : (
                        line.text
                      )}
                    </Typography>
                  ))}
                </Box>
              ))}
            </Box>
          </Card>
        </Reveal>
      </Container>

      {/* ------------------------------------------------------------------ */}
      {/* Closing call to action                                              */}
      {/* ------------------------------------------------------------------ */}
      <Box component="section" sx={{ ...steel, position: 'relative', overflow: 'hidden', textAlign: 'center' }}>
        <Box aria-hidden sx={hazardStripe(12)} />
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            left: -330,
            top: -150,
            opacity: 0.7,
            display: { xs: 'none', md: 'block' },
            pointerEvents: 'none',
          }}
        >
          <GearDrawing size={620} />
        </Box>
        <Container sx={{ position: 'relative', py: { xs: 9, md: 13 } }}>
          <AnimatedText
            component="p"
            text="Bring it in."
            highlight={['in']}
            highlightSx={{ color: glory[400] }}
            whenInView
            sx={{ ...headline, color: grey[50], fontSize: { xs: '3.75rem', md: '6.5rem' }, mb: 2.5 }}
          />
          <Reveal variants={fadeUp} delay={0.1}>
            <Typography sx={{ ...prose, color: grey[300], fontSize: '1.125rem', lineHeight: 1.6, maxWidth: '42ch', mx: 'auto', mb: 5 }}>
              Pick a time that works for you. We’ll have a bay ready and call you the minute it’s done.
            </Typography>
          </Reveal>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Button component={RouterLink} to={shopPath('book-appointment')} endIcon={<ArrowForwardIcon />} sx={redButton}>
              Book a service
            </Button>
            {telHref && (
              <Button href={telHref} startIcon={<PhoneOutlinedIcon />} sx={steelButton}>
                Call the shop
              </Button>
            )}
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;
